import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { getToken } from '../../services/authToken';
import { useLanguage } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// â”€â”€â”€ useLesson â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
async function fetchLesson(lessonId, lang = 'tr') {
  const res = await axios.get(`${API}/cabin/lessons/${lessonId}?lang=${lang}`, {
    headers: getAuthHeaders(),
  });
  // Backend may return { lesson: {...} } or plain object
  return res.data?.lesson ?? res.data;
}

export function useLesson(lessonId) {
  const { lang } = useLanguage();
  return useQuery({
    queryKey: ['cabin', 'lesson', lessonId, lang],
    queryFn: () => fetchLesson(lessonId, lang),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!lessonId && typeof window !== 'undefined' && !!getToken(),
  });
}

// â”€â”€â”€ useStartLesson â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export function useStartLesson(lessonId) {
  return useMutation({
    mutationFn: async () => {
      const res = await axios.post(
        `${API}/cabin/lessons/${lessonId}/start`,
        {},
        { headers: getAuthHeaders() }
      );
      return res.data;
    },
  });
}

// â”€â”€â”€ useSubmitPhase â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export function useSubmitPhase(lessonId) {
  return useMutation({
    mutationFn: async ({ phaseId, score, maxScore, answers, timeSpent, attemptNo }) => {
      // Backend expects `answers` as a list. Components send object summary
      // ({correct, total}) → wrap as single-item list with summary metadata.
      const answersList = Array.isArray(answers)
        ? answers
        : (answers && typeof answers === 'object' ? [answers] : []);
      const res = await axios.post(
        `${API}/cabin/lessons/${lessonId}/submit-phase`,
        {
          phase_id: phaseId,
          score,
          max_score: maxScore,
          answers: answersList,
          time_spent: timeSpent,
          attempt_no: attemptNo,
        },
        { headers: getAuthHeaders() }
      );
      return res.data;
    },
  });
}

// â”€â”€â”€ useCompleteLesson â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export function useCompleteLesson(lessonId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ attemptNo, totalScore, maxScore, totalTime }) => {
      const res = await axios.post(
        `${API}/cabin/lessons/${lessonId}/complete`,
        {
          attempt_no: attemptNo,
          total_score: totalScore,
          max_score: maxScore,
          total_time: totalTime,
        },
        { headers: getAuthHeaders() }
      );
      return res.data;
    },
    onSuccess: () => {
      // Invalidate all cabin-related caches so dashboard/progress refreshes
      queryClient.invalidateQueries({ queryKey: ['cabin', 'modules'] });
      queryClient.invalidateQueries({ queryKey: ['cabin', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['cabin', 'progress'] });
      queryClient.invalidateQueries({ queryKey: ['cabin', 'streak'] });
      queryClient.invalidateQueries({ queryKey: ['cabin', 'daily-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['cabin', 'badges'] });
      // Invalidate module detail (for lesson checkmarks without manual refresh)
      queryClient.invalidateQueries({ queryKey: ['cabin', 'module'] });
      // Invalidate the specific lesson so last_attempt status updates
      if (lessonId) {
        queryClient.invalidateQueries({ queryKey: ['cabin', 'lesson', lessonId] });
      }
    },
  });
}

// --- useCheckAnswer (Bug 3 live feedback) ------------------------------
// Tek soru dogrulugunu server-side kontrol eder. Guvenlik strip korunur:
// GET /lessons response dogru cevaplari sizdirmaz; bu endpoint yalnizca
// SORULAN sorunun is_correct + correct_answer bilgisini doner.
export function useCheckAnswer(lessonId) {
  return useMutation({
    mutationFn: async ({ phaseId, questionIndex, answer }) => {
      const res = await axios.post(
        `${API}/cabin/lessons/${lessonId}/check-answer`,
        {
          phase_id: phaseId,
          question_index: questionIndex,
          answer: answer || {},
        },
        { headers: getAuthHeaders() }
      );
      return res.data; // { is_correct, correct_answer }
    },
  });
}
